import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useMemo } from 'react'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import ViewerExistingParticipantsCard from './ViewerExistingParticipantsCard'
import ViewerNewParticipantSignupCard from './ViewerNewParticipantSignupCard'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import { resolveEventFocusActivity } from './eventFocusActivity'

type ViewerSignupCardProps = {
  event: wowaudit.Event
  activities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  isRaidPlansLoading?: boolean
}

const ViewerSignupCard = ({
  event,
  activities,
  linkedPlanDataByActivityId,
  isRaidPlansLoading = false,
}: ViewerSignupCardProps) => {
  const { team } = useTeam()
  const {
    data: { user },
  } = usePageInfo()
  const { allEntries } = useTeamEventParticipantsContext()
  const activeActivities = useMemo(() => {
    if (event.raidsPlanningScope === 'single') return activities
    return activities.filter((activity) => activity.active)
  }, [activities, event.raidsPlanningScope])
  const selectableStatuses = useMemo(
    () =>
      eventParticipantStatuses.filter((status) =>
        team.settings.raidsAvailableStatuses.value.includes(status),
      ),
    [team.settings.raidsAvailableStatuses.value],
  )
  const viewerEntries = useMemo(() => {
    if (!user?.id) return []
    return allEntries
      .filter(
        (entry) => entry.participant.characterReference.userId === user.id,
      )
      .sort((left, right) =>
        left.participant.characterReference.name.localeCompare(
          right.participant.characterReference.name,
        ),
      )
  }, [allEntries, user?.id])

  if (viewerEntries.length > 0) {
    return (
      <ViewerExistingParticipantsCard
        event={event}
        entries={viewerEntries}
        activeActivities={activeActivities}
        linkedPlanDataByActivityId={linkedPlanDataByActivityId}
        isRaidPlansLoading={isRaidPlansLoading}
        selectableStatuses={selectableStatuses}
        focusActivity={resolveEventFocusActivity(event, activities)}
      />
    )
  }

  return (
    <ViewerNewParticipantSignupCard
      event={event}
      user={user}
      selectableStatuses={selectableStatuses}
    />
  )
}

export default ViewerSignupCard
